// src/pages/ResourceDetails.jsx (UPGRADED and FINAL)

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import AxiosInstance from "../context/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import moment from "moment";
import ConflictWarningModal from "./ConflictWarningModal";
import {
  ArrowLeft,
  BookMarked,
  MapPin,
  Users,
  Clock,
  Loader2,
  XCircle,
  CalendarX,
  Send,
  Calendar as CalendarIcon,
} from "lucide-react";
import AppFooter from "./Homepage/AppFooter";

// --- Reusable UI Sub-Components (No change needed) ---
const DetailCard = ({ icon, label, value, colorClass = "text-primary-400" }) => (
  <div className="bg-surface/30 backdrop-blur-md border border-white/5 rounded-2xl p-6 transition-all hover:border-white/20 group">
    <div className={`mb-4 p-2 rounded-xl bg-white/5 w-fit group-hover:scale-110 transition-transform ${colorClass}`}>
      {icon}
    </div>
    <p className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-1">{label}</p>
    <p className="text-xl font-bold text-white tracking-tight">{value}</p>
  </div>
);

export default function ResourceDetails() {
  const { accessToken, userID } = useAppContext();
  const { resourceId } = useParams();
  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [resource, setResource] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [slots, setSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isBooking, setIsBooking] = useState(false);

  const fetchResourceDetails = useCallback(async () => {
    if (!resourceId || !accessToken) {
      setPageLoading(false);
      return;
    }
    try {
      const { data } = await AxiosInstance.get(`/organisations/resources/${resourceId}`);
      setResource(data.resource || null);
    } catch (err) {
      console.error("Error loading resource:", err);
      toast.error("Could not load resource details.");
      setResource(null);
    } finally {
      setPageLoading(false);
    }
  }, [resourceId, accessToken]);

  const fetchSlotsForDate = useCallback(async (date) => {
    if (!resourceId || !accessToken) return;
    setSlotsLoading(true);
    setSelectedSlots([]);
    try {
      const { data } = await AxiosInstance.get(`/organisations/resources/${resourceId}/slots?date=${date}`);
      setSlots((data.slots || []).filter((s) => s.available));
    } catch (err) {
      console.error("Error fetching slots:", err);
      toast.error(`Failed to load slots.`);
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [resourceId, accessToken]);

  useEffect(() => { fetchResourceDetails(); }, [fetchResourceDetails]);
  useEffect(() => { fetchSlotsForDate(selectedDate); }, [selectedDate, fetchSlotsForDate]);

  const toggleSlot = (slot) => {
    setSelectedSlots((prev) =>
      prev.some((s) => s.start === slot.start)
        ? prev.filter((s) => s.start !== slot.start)
        : [...prev, slot]
    );
  };

  const finalizeBooking = async () => {
    const bookingToast = toast.loading("Finalizing booking...");
    try {
      await AxiosInstance.post(`/organisations/resources/${resourceId}/bookings/`, {
        date: selectedDate,
        slots: selectedSlots,
        userId: userID,
      });
      toast.success("Booking Successful!", { id: bookingToast });
      fetchSlotsForDate(selectedDate);
      setSelectedSlots([]);
    } catch (err) {
      toast.error(err.response?.data?.error || "Booking failed.", { id: bookingToast });
    }
  };

  const handleBookingAttempt = async () => {
    if (selectedSlots.length === 0) return toast.error("Select at least one slot.");
    if (!accessToken || !userID) return toast.error("Log in to book.");

    setIsBooking(true);
    try {
      const sortedSlots = [...selectedSlots].sort((a, b) => a.start.localeCompare(b.start));
      const startTime = moment(`${selectedDate} ${sortedSlots[0].start}`, "YYYY-MM-DD HH:mm").utc().toISOString();
      const endTime = moment(`${selectedDate} ${sortedSlots[sortedSlots.length - 1].end}`, "YYYY-MM-DD HH:mm").utc().toISOString();

      const { data } = await AxiosInstance.post("/timeline/conflicts/", {
        start_time: startTime,
        end_time: endTime,
      });

      if (data.has_conflict) {
        setConflictData(data);
        setShowConflictModal(true);
      } else {
        await finalizeBooking();
      }
    } catch (err) {
      toast.error("Conflict check failed.");
    } finally {
      setIsBooking(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="bg-background min-h-screen flex items-center justify-center">
        <Loader2 size={48} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="bg-background text-white min-h-screen flex flex-col items-center justify-center p-8">
        <XCircle size={64} className="text-red-500 mb-4" />
        <h1 className="text-3xl font-bold mb-4">Resource Not Found</h1>
        <button onClick={() => navigate(-1)} className="bg-white text-black px-8 py-3 rounded-full font-bold">Go Back</button>
      </div>
    );
  }

  return (
    <div className="bg-background text-white min-h-screen font-sans selection:bg-primary-500 selection:text-white flex flex-col overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
         <div className="absolute -top-[10%] left-[20%] w-[800px] h-[800px] bg-primary-500/10 rounded-full blur-[160px]" />
         <div className="absolute -bottom-[10%] right-[10%] w-[600px] h-[600px] bg-secondary-900/10 rounded-full blur-[160px]" />
      </div>

      <main className="flex-grow relative z-10">
        <div className="container mx-auto px-6 pt-12">
          <button onClick={() => navigate(-1)} className="group flex items-center gap-3 text-sm text-gray-500 hover:text-white transition-all uppercase tracking-widest font-bold mb-16">
            <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-white/30 group-hover:bg-white/5 transition-all">
              <ArrowLeft size={14} />
            </div>
            Back
          </button>

          <header className="max-w-4xl mb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-4">Resource Allocation</span>
              <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-6 leading-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40">{resource.name}</h1>
              <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-medium max-w-2xl">{resource.description}</p>
            </motion.div>
          </header>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16">
              <DetailCard icon={<MapPin size={24} />} label="Location" value={resource.location} colorClass="text-emerald-400" />
              <DetailCard icon={<Users size={24} />} label="Capacity" value={`${resource.capacity} People`} colorClass="text-indigo-400" />
              <DetailCard icon={<BookMarked size={24} />} label="Status" value="Open for Booking" colorClass="text-primary-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-12 xl:col-span-7 space-y-16">
               <section>
                  <div className="flex items-center gap-4 mb-10">
                    <h2 className="text-3xl font-black tracking-tight">Booking Dashboard</h2>
                    <div className="h-[1px] flex-grow bg-white/10"></div>
                  </div>
                  
                  <div className="bg-surface/30 backdrop-blur-xl border border-white/10 rounded-[2rem] p-8 md:p-10 shadow-2xl relative overflow-hidden">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                      <div>
                        <h3 className="text-lg font-bold mb-1">Select your date</h3>
                        <p className="text-xs text-gray-400">Choose a day to see available time slots</p>
                      </div>
                      <div className="relative">
                        <CalendarIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400" />
                        <input
                          type="date"
                          value={selectedDate}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="pl-10 pr-4 h-12 bg-white/5 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-primary-500 focus:outline-none transition-all hover:bg-white/10 appearance-none min-w-[180px] text-sm"
                        />
                      </div>
                    </div>

                    <div className="min-h-[200px]">
                      {slotsLoading ? (
                        <div className="flex flex-col items-center justify-center h-[200px] text-gray-500">
                          <Loader2 size={32} className="animate-spin text-primary-500 mb-4" />
                          <p className="font-medium">Syncing availability...</p>
                        </div>
                      ) : slots.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[300px] text-gray-500 bg-white/5 rounded-3xl border border-white/5">
                          <CalendarX size={48} className="mb-4 opacity-50" />
                          <p className="font-bold text-lg text-white">Fully Booked</p>
                          <p className="text-sm">No slots available for {moment(selectedDate).format("MMMM Do")}</p>
                        </div>
                      ) : (
                        <div>
                           <div className="flex items-center justify-between mb-8">
                              <h4 className="text-sm font-bold uppercase tracking-widest text-gray-500">Available Windows</h4>
                              <span className="text-xs px-3 py-1 bg-white/10 rounded-full font-bold">{slots.length} Slots</span>
                           </div>
                           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {slots.map((slot, i) => {
                              const isSelected = selectedSlots.some((s) => s.start === slot.start);
                              return (
                                <button
                                  key={i}
                                  onClick={() => toggleSlot(slot)}
                                  className={`group relative p-4 rounded-2xl transition-all duration-300 border ${
                                    isSelected
                                      ? "bg-primary-500 border-primary-500 text-white shadow-xl shadow-primary-500/30 scale-105 z-10"
                                      : "bg-white/5 border-white/5 text-gray-400 hover:bg-white/10 hover:border-white/20 hover:text-white"
                                  }`}
                                >
                                  <div className="flex flex-col items-center gap-2">
                                    <Clock size={16} className={isSelected ? "text-white" : "text-primary-500/50 group-hover:text-primary-400"} />
                                    <span className="text-sm font-black">{slot.start}</span>
                                    <span className="text-[10px] uppercase tracking-tighter opacity-50">{slot.end}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
               </section>
            </div>

            <aside className="lg:col-span-12 xl:col-span-5 mt-16 xl:mt-0">
               <div className="xl:sticky xl:top-24">
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-1 rounded-[3rem] bg-gradient-to-br from-white/20 via-white/10 to-transparent shadow-3xl">
                    <div className="bg-[#0f0f0f] rounded-[2.8rem] p-10 relative overflow-hidden">
                       <div className="absolute top-0 left-0 w-32 h-32 bg-primary-500/10 rounded-full blur-[60px] -ml-16 -mt-16" />
                       <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-8">Reservation Summary</h3>
                       
                       <div className="space-y-6 mb-12">
                          <div className="flex justify-between items-end">
                            <span className="text-gray-400 font-medium">Date</span>
                            <span className="text-xl font-bold">{moment(selectedDate).format("MMM D, YYYY")}</span>
                          </div>
                          <div className="flex justify-between items-end">
                            <span className="text-gray-400 font-medium">Selected Slots</span>
                            <span className="text-xl font-bold">{selectedSlots.length}</span>
                          </div>
                          <div className="h-[1px] bg-white/10" />
                          <div className="pt-2">
                            <p className="text-gray-500 text-xs mb-4 uppercase font-bold tracking-widest">Selected Times</p>
                            <div className="flex flex-wrap gap-2">
                               {selectedSlots.length > 0 ? selectedSlots.map(s => (
                                 <span key={s.start} className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold border border-white/10">{s.start}</span>
                               )) : <span className="text-gray-600 italic text-sm">No slots selected</span>}
                            </div>
                          </div>
                       </div>

                       <button
                         onClick={handleBookingAttempt}
                         disabled={selectedSlots.length === 0 || isBooking || slotsLoading}
                         className="w-full bg-white text-black font-black py-6 rounded-[2rem] text-xl hover:bg-primary-400 hover:text-white transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
                       >
                         {isBooking ? <Loader2 size={24} className="animate-spin" /> : <Send size={20} />}
                         Reserve Selection
                       </button>
                    </div>
                  </motion.div>
               </div>
            </aside>
          </div>
        </div>
      </main>

      <div className="container mx-auto px-6 py-12 mt-auto">
        <AppFooter />
      </div>

      <AnimatePresence>
        {showConflictModal && (
          <ConflictWarningModal
            newActionTitle={`Reserving ${resource.name}`}
            conflicts={conflictData.conflicting_items}
            onConfirmOverride={async () => {
              setShowConflictModal(false);
              await finalizeBooking();
            }}
            onCancel={() => setShowConflictModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
